import React, { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  Chip,
  AccordionDetails,
  MenuItem,
  Checkbox,
  Grid,
  CircularProgress,
  FormControl,
  ListItemText,
  Select,
  OutlinedInput,
  DialogActions,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PageContainer from "../../../components/pageContainer";
import { PictureAsPdf, TableView, ExpandMore, Add, Delete, FileUpload } from "@mui/icons-material";
import useReport from "../../../hooks/useReport";
import { AdminUpdateReportAdminReportReportIdPutData, PhotoSetResponse, ReportResponse } from "../../../client";
import { downloadPdf, downloadExcel, downloadDefaultPdf } from "../../../utils/downloadUtils";
import ImageSelector from "../../../components/imageSelector";
import FixedAspectImage from "../../../components/fixedAspectImage";
import { compressImage } from "../../../utils/imageCompression";
import { MenuProps } from "../../../constants";
import { StyledAccordion, StyledAccordionSummary, StyledTableCell } from "../../../styles/commonStyles";
import { useWorkTypeOptions, useSalesPersonOptions, useWorkTypeOptionsWithId, useSalesPersonOptionsWithId } from "../../../hooks/useMaster";
import { ImageChange, PhotoSetChangesWithMiddle, PhotoUpdateWithMiddle } from "../../../types/photo";
import { isValidFieldValue } from "../../../utils/typeGuards";
import { useFormComponents } from "../../../hooks/useFormComponents";
import { formatWorkValidationMessage, validateOtherWorks } from "../../../utils/reportWorkValidation";


const ReportFreeDetail: FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<ReportResponse | null>(null);
  const { id } = useParams();
  const { getReportById, updateReport, getImageUploadUrl, deleteFile, updateReportStatus, updateReportSalesStatus } = useReport();
  const { options: workTypeOptions, loading: workTypeLoading } = useWorkTypeOptions();
  const { options: salesPersonOptions, loading: salesPersonLoading } = useSalesPersonOptions();
  const { optionsWithId: workTypeOptionsWithId } = useWorkTypeOptionsWithId();
  const { optionsWithId: salesPersonOptionsWithId } = useSalesPersonOptionsWithId();
  const idNum = Number(id);
  const [originalData, setOriginalData] = useState<ReportResponse | null>(null);
  const [photoChanges, setPhotoChanges] = useState<Map<number, PhotoSetChangesWithMiddle>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'worker' | 'other_work' | 'other' | 'photo' | 'food_photo' | 'food_unit' | 'food_photo_set' | 'other_photo_set';
    index: number;
    parentIndex?: number;
  } | null>(null);
  const [expandedUnits, setExpandedUnits] = useState<Record<string, boolean>>({});
  const { renderEditableDate } = useFormComponents(isEditing);


  // 削除確認ダイアログを開く関数
  const handleDeleteClick = (
    type: 'worker' | 'other_work' | 'other' | 'photo' | 'food_photo' | 'food_unit' | 'food_photo_set' | 'other_photo_set',
    index: number,
    parentIndex?: number
  ) => {
    setDeleteTarget({ type, index, parentIndex });
    setDeleteDialogOpen(true);
  };

  // 削除を実行する関数
  const handleDelete = () => {
    if (!deleteTarget || !data) return;

    switch (deleteTarget.type) {
      case 'worker':
        if (data.workers) {
          const newWorkers = [...data.workers];
          newWorkers.splice(deleteTarget.index, 1);
          setData({ ...data, workers: newWorkers });
        }
        break;
      case 'other_work':
        if (data.other_works) {
          const newWorks = [...data.other_works];
          newWorks.splice(deleteTarget.index, 1);
          setData({ ...data, other_works: newWorks });
        }
        break;
      case 'other':
        if (data.other_works) {
          const newWorks = [...data.other_works];
          newWorks.splice(deleteTarget.index, 1);
          setData({ ...data, other_works: newWorks });
        }
        break;
      case 'photo':
        if (data.photo_sets) {
          const newPhotos = [...data.photo_sets];
          newPhotos.splice(deleteTarget.index, 1);
          setData({ ...data, photo_sets: newPhotos });
        }
        break;
      case 'food_photo':
        if (data.food_photo_sets) {
          const newPhotos = [...data.food_photo_sets];
          newPhotos.splice(deleteTarget.index, 1);
          setData({ ...data, food_photo_sets: newPhotos });
        }
        break;
      case 'food_unit':
        if (data.food_units) {
          const newUnits = [...data.food_units];
          newUnits.splice(deleteTarget.index, 1);
          setData({ ...data, food_units: newUnits });
        }
        break;
      case 'food_photo_set':
        // フードユニット内の個別写真セットを削除
        if (data.food_units && deleteTarget.parentIndex !== undefined) {
          const newUnits = [...data.food_units];
          const unit = newUnits[deleteTarget.parentIndex];
          if (unit.photo_sets) {
            const newPhotoSets = [...unit.photo_sets];
            newPhotoSets.splice(deleteTarget.index, 1);
            newUnits[deleteTarget.parentIndex] = { ...unit, photo_sets: newPhotoSets };
            setData({ ...data, food_units: newUnits });
          }
        }
        break;
      case 'other_photo_set':
        // その他作業内の個別写真セットを削除
        if (data.other_works && deleteTarget.parentIndex !== undefined) {
          const newWorks = [...data.other_works];
          const work = newWorks[deleteTarget.parentIndex];
          if (work.photo_sets) {
            const newPhotoSets = [...work.photo_sets];
            newPhotoSets.splice(deleteTarget.index, 1);
            newWorks[deleteTarget.parentIndex] = { ...work, photo_sets: newPhotoSets };
            setData({ ...data, other_works: newWorks });
          }
        }
        break;

    }
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  // 削除ダイアログのコンポーネント
  const DeleteConfirmDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
    >
      <DialogTitle>削除の確認</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {deleteTarget && `この${deleteTarget.type === 'worker' ? '作業者' :
            deleteTarget.type === 'other_work' ? 'その他作業' :
              deleteTarget.type === 'food_photo' ? 'フード写真' :
                deleteTarget.type === 'food_unit' ? 'フード' :
                  deleteTarget.type === 'food_photo_set' ? 'フード写真セット' :
                    deleteTarget.type === 'other' ? 'その他作業' :
                      'ガラス・その他の写真'
            }を削除してもよろしいですか？`}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
        <Button onClick={handleDelete} color="error">削除</Button>
      </DialogActions>
    </Dialog>
  );

  const SectionHeader = ({ index, type }: {
    index: number,
    type: 'worker' | 'other_work' | 'other' | 'photo' | 'food_photo' | 'food_unit'
  }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
    >
      <Typography>
        {type === 'worker' ? `作業者 ${index + 1}` :
          type === 'other_work' ? `その他作業 ${index + 1}` :
            type === 'food_photo' ? `フード写真 ${index + 1}` :
              type === 'food_unit' ? `フード ${index + 1}` :
                type === 'other' ? `作業 ${index + 1}` :
                  `写真セット ${index + 1}`}
      </Typography>
      {isEditing && (
        <Button
          startIcon={<Delete />}
          color="error"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(type, index);
          }}
          sx={{ ml: 2 }}
        >
          削除
        </Button>
      )}
    </Box>
  );


  useEffect(() => {
    if (idNum) {
      getData();
    }
  }, [idNum]);

  const getData = async () => {
    if (!idNum) return;
    try {
      const res = await getReportById({ reportId: idNum });
      if (res) {
        setData(res);
        setOriginalData(res); // 元データも保存
      }
    } catch (error) {
      console.error("Failed to get report detail", error);
    }
  };

  // キャンセル処理を追加
  const handleCancel = () => {
    if (originalData) {
      setData(JSON.parse(JSON.stringify(originalData))); // ディープコピーを作成
    }
    // 編集中に選択した未保存の写真変更(photoChanges)も破棄する。
    // クリアしないと、キャンセル後に再編集して保存した際に、
    // 前回キャンセルしたはずの画像がアップロードされてしまう。
    setPhotoChanges(new Map());
    setIsEditing(false);
  };

  // 編集モード開始時の処理を追加
  const handleStartEditing = () => {
    setOriginalData(JSON.parse(JSON.stringify(data))); // 現在のデータのディープコピーを保存
    setIsEditing(true);
  };


  // PDFダウンロードの処理
  const handleDownloadPdf = async () => {
    if (!data) return;

    await downloadPdf(
      {
        id: idNum,
        destinationName: data.delivery_destination,
        workDate: data.start_date,
        setLoading: setIsLoading
      },
      'report'
    );
  };

  const handleDownloadDefaultPdf = async () => {
    if (!data) return;

    await downloadDefaultPdf(
      {
        id: idNum,
        destinationName: data.delivery_destination,
        workDate: data.start_date,
        setLoading: setIsLoading
      },
      'report'
    );
  };

  const handleDownloadExcel = async () => {
    if (!data) return;

    await downloadExcel(
      {
        id: idNum,
        destinationName: data.delivery_destination,
        workDate: data.start_date,
        setLoading: setIsLoading
      },
      'report'
    );
  };


  const handlePhotoChange = async (
    photoSet: PhotoSetResponse,
    type: 'before' | 'middle' | 'after',
    file: File | null,
    onChange: (updatedPhotoSet: PhotoSetResponse) => void  // onChange パラメータを追加
  ) => {
    const changes = photoChanges.get(photoSet.id) || {} as PhotoSetChangesWithMiddle;

    // S3のURLのみを保持し、Blob URLは保持しない
    const getValidS3Url = (url: string | null | undefined): string | null => {
      if (!url) return null;
      return url.startsWith('https://') ? url : null;
    };

    if (type === 'before') {
      changes.before = {
        oldUrl: getValidS3Url(photoSet.before_image_path),
        newFile: file,
        newUrl: null
      };
    } else if (type === 'middle') {
      changes.middle = {
        oldUrl: getValidS3Url(photoSet.middle_image_path),
        newFile: file,
        newUrl: null
      };
    } else {
      changes.after = {
        oldUrl: getValidS3Url(photoSet.after_image_path),
        newFile: file,
        newUrl: null
      };
    }

    setPhotoChanges(new Map(photoChanges.set(photoSet.id, changes)));

    // UIの更新用に一時的なBlob URLを生成
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      if (type === 'before') {
        onChange({
          ...photoSet,
          before_image_path: blobUrl
        });
      } else if (type === 'middle') {
        onChange({
          ...photoSet,
          middle_image_path: blobUrl
        });
      } else {
        onChange({
          ...photoSet,
          after_image_path: blobUrl
        });
      }
    }
  };

  // Blob URLのクリーンアップ用関数
  const cleanupBlobUrl = (url: string | null | undefined) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  // 画像削除時に、その写真セットの保留中の変更(未アップロードの新規画像)を破棄する。
  // これを呼ばずに写真だけnullにすると、新規選択→削除の順で操作した際にphotoChangesに
  // 残った新規ファイルがsubmit時にアップロードされ、削除したはずの画像が復活してしまう。
  const clearPhotoChange = (photoSetId: number, type: 'before' | 'middle' | 'after') => {
    setPhotoChanges(prev => {
      const changes = prev.get(photoSetId);
      if (!changes || !changes[type]) return prev;
      const next = new Map(prev);
      const updated = { ...changes };
      delete updated[type];
      next.set(photoSetId, updated);
      return next;
    });
  };

  // 常に最新のdataを参照するためのref（クリーンアップをマウント時の1回だけ登録するため）
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const revokeAllPhotoBlobs = (target: ReportResponse | null | undefined) => {
    target?.photo_sets?.forEach(photo => {
      cleanupBlobUrl(photo.before_image_path);
      cleanupBlobUrl(photo.middle_image_path);
      cleanupBlobUrl(photo.after_image_path);
    });
    // フードユニット内の写真もクリーンアップ
    target?.food_units?.forEach(unit => {
      unit.photo_sets?.forEach(photo => {
        cleanupBlobUrl(photo.before_image_path);
        cleanupBlobUrl(photo.middle_image_path);
        cleanupBlobUrl(photo.after_image_path);
      });
    });
    // その他作業内の写真もクリーンアップ
    target?.other_works?.forEach(work => {
      work.photo_sets?.forEach(photo => {
        cleanupBlobUrl(photo.before_image_path);
        cleanupBlobUrl(photo.middle_image_path);
        cleanupBlobUrl(photo.after_image_path);
      });
    });
  };

  // コンポーネントのアンマウント時にBlob URLを解放。
  // 依存配列を[data]にすると編集ごとに前回分のcleanupが走り、表示中の
  // 同一blob URLを誤って解放してしまう（プレビューが壊れる）ため、
  // マウント時の1回だけ登録し、アンマウント時にrefから最新のdataを読む。
  useEffect(() => {
    return () => {
      revokeAllPhotoBlobs(dataRef.current);
    };
  }, []);

  // 同一ルート内でidNum（表示中のreport）が切り替わった場合にも、
  // 切り替え前のreportのBlob URLを解放する。このページはreport一覧を経由せず
  // idだけ変わる遷移をした場合コンポーネントが再マウントされないため、
  // アンマウント時のcleanupだけでは切り替え前のblobが解放されずに残ってしまう。
  useEffect(() => {
    return () => {
      revokeAllPhotoBlobs(dataRef.current);
    };
  }, [idNum]);

  const uploadPhoto = async (change: ImageChange): Promise<string | null> => {
    if (!change.newFile) return null;

    try {
      // 画像を圧縮（パラメータは関数内で固定）
      const compressResult = await compressImage(change.newFile);

      // 圧縮された画像ファイルを使用
      const compressedFile = compressResult.file;

      const uploadUrlResponse = await getImageUploadUrl({
        filename: change.newFile.name
      });

      if (!uploadUrlResponse?.upload_url) {
        console.error('No upload URL received');
        return null;
      }

      const { url, fields } = uploadUrlResponse.upload_url;
      // FormDataの作成
      const formData = new FormData();
      // 認証フィールドを追加 - 型エラーを修正
      Object.entries(fields).forEach(([key, value]) => {
        // value が string型であることを確認
        formData.append(key, value as string);
      });
      // 圧縮されたファイルを追加
      formData.append('file', compressedFile);

      // S3へのアップロード
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      // アップロード成功後のURLを構築
      const finalUrl = `${url}${fields.key}`;
      return finalUrl;

    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // 写真セット情報の更新処理関数は同じ
  const getUpdatedPhotoSets = (photoSets: PhotoSetResponse[] | null | undefined): PhotoUpdateWithMiddle[] => {
    if (!photoSets) return [];


    return photoSets.map(photo => {
      const changes = photoChanges.get(photo.id);

      // before_image_path の処理
      let beforeImagePath: string | null = null;
      if (changes?.before?.newUrl && typeof changes.before.newUrl === 'string') {
        beforeImagePath = changes.before.newUrl;
      } else if (typeof photo.before_image_path === 'string') {
        if (photo.before_image_path.startsWith('https://')) {
          beforeImagePath = photo.before_image_path;
        } else {
          console.log('Invalid before URL format:', photo.before_image_path);
        }
      }

      // middle_image_path の処理
      let middleImagePath: string | null = null;
      if (changes?.middle?.newUrl && typeof changes.middle.newUrl === 'string') {
        middleImagePath = changes.middle.newUrl;
      } else if (typeof photo.middle_image_path === 'string') {
        if (photo.middle_image_path.startsWith('https://')) {
          middleImagePath = photo.middle_image_path;
        } else {
          console.log('Invalid middle URL format:', photo.middle_image_path);
        }
      }

      // after_image_path の処理
      let afterImagePath: string | null = null;
      if (changes?.after?.newUrl && typeof changes.after.newUrl === 'string') {
        afterImagePath = changes.after.newUrl;
      } else if (typeof photo.after_image_path === 'string') {
        if (photo.after_image_path.startsWith('https://')) {
          afterImagePath = photo.after_image_path;
        } else {
          console.log('Invalid after URL format:', photo.after_image_path);
        }
      }

      const result = {
        title: photo.title || '',
        before_image_path: beforeImagePath,
        before_comment: photo.before_comment || '',
        middle_image_path: middleImagePath,
        middle_comment: photo.middle_comment || '',
        after_image_path: afterImagePath,
        after_comment: photo.after_comment || ''
      };

      return result;
    });
  };

  const submit = async () => {
    if (!data || !idNum) return;

    // 作業内容記入欄の必須チェック（未入力のままの保存を防ぐ）
    const otherWorkErrors = validateOtherWorks(
      data.other_works,
      originalData?.other_works?.map((work) => work.id)
    );
    if (otherWorkErrors.length > 0) {
      alert(formatWorkValidationMessage(otherWorkErrors));
      return;
    }

    setIsLoading(true);

    try {
      // 1. 写真の更新処理
      for (const [photoSetId, changes] of Array.from(photoChanges.entries())) {

        // Before画像の処理
        if (changes.before?.newFile) {
          // 古い画像の削除
          if (changes.before.oldUrl) {
            await deleteFile({ fileUrl: changes.before.oldUrl });
          }

          changes.before.newUrl = await uploadPhoto(changes.before);
        }

        // Middle画像の処理
        if (changes.middle?.newFile) {
          // 古い画像の削除
          if (changes.middle.oldUrl) {
            await deleteFile({ fileUrl: changes.middle.oldUrl });
          }

          changes.middle.newUrl = await uploadPhoto(changes.middle);
        }

        // After画像の処理
        if (changes.after?.newFile) {
          // 古い画像の削除
          if (changes.after.oldUrl) {
            await deleteFile({ fileUrl: changes.after.oldUrl });
          }

          changes.after.newUrl = await uploadPhoto(changes.after);
        }
      }


      const updateData: AdminUpdateReportAdminReportReportIdPutData = {
        reportId: idNum,
        requestBody: {
          delivery_destination: data.delivery_destination || '',
          address1: data.address1 || '',
          start_date: data.start_date || new Date().toISOString(),
          end_date: data.end_date || new Date().toISOString(),
          sales_person: data.sales_person || '',
          sales_person_id: data.sales_person_id || null,
          indoor_units: data.indoor_units || 0,
          outdoor_units: data.outdoor_units || 0,
          indoor_rooms: data.indoor_rooms || 0,
          outdoor_rooms: data.outdoor_rooms || 0,
          responsible_person: data.responsible_person || '',
          points_to_note: data.points_to_note || '',
          worker_memo: data.worker_memo || '',
          memo: data.memo || '',
          work_result: data.work_result === "継続" ? "継続" : "完了",
          status: data.status || "未対応",
          sales_status: data.sales_status || "未対応",
          signature: data.signature || null,

          // 作業者情報
          workers: data.workers?.map(worker => ({
            worker_name: worker.worker_name || '',
            company_name: worker.company_name || '',
          })) || [],

          // その他作業情報
          other_works: data.other_works?.map(work => ({
            work_type: work.work_type || '',
            work_type_id: work.work_type_id || null,
            details: work.details || '',
            quantity: work.quantity || null,
            unit: work.unit || null,
            photo_sets: getUpdatedPhotoSets(work.photo_sets),
          })) || [],

          // 写真セット情報
          photo_sets: getUpdatedPhotoSets(data.photo_sets),
          // フードユニット情報
          food_units: data.food_units?.map(unit => ({
            management_no: unit.management_no || '',
            photo_sets: getUpdatedPhotoSets(unit.photo_sets),
          })) || [],
        }
      };


      const response = await updateReport(updateData);

      if (response) {
        setPhotoChanges(new Map());
        setIsEditing(false);
        await getData();
      } else {
        alert("更新に失敗しました。再度お試しください。");
      }

    } catch (error) {
      console.error("Failed to update report", error);
      alert("更新に失敗しました。再度お試しください。");
    } finally {
      setIsLoading(false); // 処理完了時にローディングを非表示
    }
  };


  const addWorker = () => {
    if (!data || !data.workers) return;
    const newWorker = {
      id: Date.now(), // temporary ID
      worker_name: "",
      company_name: "",
      start_date: new Date().toISOString(),
      end_date: new Date().toISOString()
    };
    setData({ ...data, workers: [...data.workers, newWorker] });
  };

  const addOtherWork = () => {
    if (!data) return;
    const newWork = {
      id: Date.now(),
      work_type: "",
      details: "",
      quantity: null,
      unit: null,
      photo_sets: []
    };
    setData({ ...data, other_works: [...(data.other_works || []), newWork] });
  };

  const addPhotoSet = () => {
    if (!data || !data.photo_sets) return;
    const newPhotoSet = {
      id: Date.now(),
      title: "",
      before_image_path: null,
      before_comment: "",
      middle_image_path: null,
      middle_comment: "",
      after_image_path: null,
      after_comment: ""
    };
    setData({ ...data, photo_sets: [...data.photo_sets, newPhotoSet] });
  };

  const addFoodUnit = () => {
    if (!data || !data.food_units) return;
    const newUnit = {
      id: Date.now(),
      management_no: "",
      photo_sets: []
    };
    setData({ ...data, food_units: [...data.food_units, newUnit] });
  };

  const getIndoorUnitManagementNumbers = () => {
    if (!data?.indoor_units_list) return [];
    return data.indoor_units_list
      .map(unit => unit.management_no)
      .filter(no => no)
      .sort();
  };

  const renderEditableField = (
    value: any,
    onChange: (value: any) => void,
    type: 'text' | 'number' | 'array' = 'text'
  ) => {
    const validValue = isValidFieldValue(value) ? value : undefined;

    if (isEditing) {
      if (type === 'array') {
        const indoorUnitNumbers = getIndoorUnitManagementNumbers();
        return (
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={Array.isArray(value) ? value : []}
              onChange={(e) => {
                const selectedValues = typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value;
                onChange(selectedValues);
              }}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                Array.isArray(selected) ? selected.join(', ') : ''
              )}
              MenuProps={MenuProps}
            >
              {indoorUnitNumbers.map((number) => (
                <MenuItem key={number} value={number as string}>
                  <Checkbox checked={Array.isArray(value) && value.includes(number)} />
                  <ListItemText primary={number} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      return (
        <TextField
          fullWidth
          value={validValue?.toString() || ''}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          type={type}
        />
      );
    }
    return Array.isArray(value) ? value.join(', ') : (validValue?.toString() || '');
  };

  const renderPhotoSetContent = (
    photoSet: PhotoSetResponse,
    onChange: (updatedPhotoSet: PhotoSetResponse) => void
  ) => {
    // 画像のアスペクト比を設定 (4:3)
    const imageAspectRatio = 4 / 3; // ≈ 1.33
    // 画像表示の統一サイズ設定
    const imageDisplayHeight = 240; // 高さを固定値に設定（ピクセル）

    return (
      <Box>
        <Grid container spacing={2}>
          {/* Before写真エリア */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業前
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.before_image_path}
                  alt="Before"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.before_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'before', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        before_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.before_image_path);
                      clearPhotoChange(photoSet.id, 'before');
                      onChange({
                        ...photoSet,
                        before_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}

            </Box>
          </Grid>

          {/* Middle写真エリア */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業中
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.middle_image_path}
                  alt="Middle"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.middle_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'middle', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        middle_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.middle_image_path);
                      clearPhotoChange(photoSet.id, 'middle');
                      onChange({
                        ...photoSet,
                        middle_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {/* After写真エリア */}
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                作業後
              </Typography>
              <Box mb={2}>
                <FixedAspectImage
                  imageUrl={photoSet.after_image_path}
                  alt="After"
                  aspectRatio={imageAspectRatio}
                  height={imageDisplayHeight}
                />
              </Box>
              {isEditing && (
                <Box mb={2}>
                  <ImageSelector
                    existingImageUrl={photoSet.after_image_path || null}
                    onImageSelected={(file, imageUrl) => {
                      handlePhotoChange(photoSet, 'after', file, onChange);
                      // 一時的に表示用URLを設定
                      onChange({
                        ...photoSet,
                        after_image_path: imageUrl
                      });
                    }}
                    onDeleteImage={() => {
                      cleanupBlobUrl(photoSet.after_image_path);
                      clearPhotoChange(photoSet.id, 'after');
                      onChange({
                        ...photoSet,
                        after_image_path: null
                      });
                    }}
                    aspectRatio={imageAspectRatio}
                    buttonLabel="写真を選択"
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderSectionFooter = (onAdd: () => void, buttonText: string) => (
    isEditing && (
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={onAdd}
          startIcon={<Add />}
          size="small"
        >
          {buttonText}
        </Button>
      </Box>
    )
  );

  return (
    <PageContainer>
      <DeleteConfirmDialog />
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {!data ? (
        <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {/* ヘッダー */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="div">フリー報告書詳細</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {isEditing && (
                <>
                  <Button variant="outlined" onClick={handleCancel}>
                    キャンセル
                  </Button>
                  <Button variant="contained" color="primary" onClick={submit}>
                    保存
                  </Button>
                </>
              )}
              {!isEditing && (
                <>
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* 営業ステータス - バッジスタイル */}
                    <FormControl size="small">
                      <InputLabel
                        sx={{
                          fontSize: '0.75rem',
                          transform: 'translate(14px, -9px) scale(0.75)',
                          bgcolor: 'background.paper',
                          px: 0.5
                        }}
                      >
                        営業ステータス
                      </InputLabel>
                      <Select
                        value={data?.sales_status}
                        onChange={async (e) => {
                          try {
                            await updateReportSalesStatus({
                              reportId: idNum,
                            });
                            setData(prevData =>
                              prevData ? {
                                ...prevData,
                                sales_status: e.target.value
                              } : null
                            );
                          } catch (error) {
                            console.error("営業ステータス更新に失敗しました:", error);
                            alert("営業ステータスの更新に失敗しました。再度お試しください。");
                          }
                        }}
                        sx={{
                          minWidth: 110,
                          '& .MuiSelect-select': {
                            color: data?.sales_status === "未対応" ? 'warning.main' : 'success.main',
                            fontWeight: 'bold',
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="未対応">未対応</MenuItem>
                        <MenuItem value="対応済み">対応済み</MenuItem>
                      </Select>
                    </FormControl>

                    {/* 報告書ステータス - バッジスタイル */}
                    <FormControl size="small">
                      <InputLabel
                        sx={{
                          fontSize: '0.75rem',
                          transform: 'translate(14px, -9px) scale(0.75)',
                          bgcolor: 'background.paper',
                          px: 0.5
                        }}
                      >
                        報告書ステータス
                      </InputLabel>
                      <Select
                        value={data?.status}
                        onChange={async (e) => {
                          try {
                            await updateReportStatus({
                              reportId: idNum,
                            });
                            setData(prevData =>
                              prevData ? {
                                ...prevData,
                                status: e.target.value
                              } : null
                            );
                          } catch (error) {
                            console.error("ステータス更新に失敗しました:", error);
                            alert("ステータスの更新に失敗しました。再度お試しください。");
                          }
                        }}
                        sx={{
                          minWidth: 110,
                          '& .MuiSelect-select': {
                            color: data?.status === "未対応" ? 'warning.main' : 'success.main',
                            fontWeight: 'bold',
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="未対応">未対応</MenuItem>
                        <MenuItem value="対応済み">対応済み</MenuItem>
                      </Select>
                    </FormControl>

                    {/* アクションボタン群 */}
                    <Box display="flex" gap={1}>
                      {data.pdf_url && (
                        <Button
                          startIcon={<FileUpload />}
                          variant="outlined"
                          onClick={handleDownloadDefaultPdf}
                          size="small"
                        >
                          現場提出時PDF出力
                        </Button>
                      )}
                      <Button
                        startIcon={<PictureAsPdf />}
                        variant="outlined"
                        onClick={handleDownloadPdf}
                        size="small"
                      >
                        PDF出力
                      </Button>
                      <Button
                        startIcon={<TableView />}
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        size="small"
                      >
                        Excel出力
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleStartEditing}
                      >
                        編集
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>


          {/* 基本情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">基本情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  {/* 通常の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      納入先
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.delivery_destination,
                        (value) => setData({ ...data, delivery_destination: value })
                      )}
                    </TableCell>
                  </TableRow>

                  {/* 住所の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      住所
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.address1,
                        (value) => setData({ ...data, address1: value })
                      )}
                    </TableCell>
                  </TableRow>
                  {/* 日時の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業日時
                    </StyledTableCell>
                    <TableCell>
                      <Box display="flex" gap={2}>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">開始日時</Typography>
                          {renderEditableDate(
                            data.start_date,
                            (dateString) => setData({
                              ...data,
                              start_date: dateString
                            })
                          )}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="textSecondary">終了日時</Typography>
                          {renderEditableDate(
                            data.end_date,
                            (dateString) => setData({
                              ...data,
                              end_date: dateString
                            })
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* その他の通常の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業責任者
                    </StyledTableCell>
                    <TableCell>
                      {renderEditableField(
                        data.responsible_person,
                        (value) => setData({ ...data, responsible_person: value })
                      )}
                    </TableCell>
                  </TableRow>

                  {/* 作業結果の行 */}
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業結果
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          select
                          value={data.work_result}
                          onChange={(e) => setData({ ...data, work_result: e.target.value })}
                          fullWidth
                          size="small"
                        >
                          <MenuItem value="完了">完了</MenuItem>
                          <MenuItem value="継続">継続</MenuItem>
                        </TextField>
                      ) : (
                        <Chip
                          label={data.work_result}
                          color={data.work_result === "完了" ? "success" : "warning"}
                        />
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      営業担当者
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <Autocomplete
                          value={data.sales_person || ""}
                          inputValue={data.sales_person || ""}
                          onChange={(event, newValue) => {
                            // マスタデータから選択された場合はIDも設定
                            const selectedMaster = salesPersonOptionsWithId.find(option => option.name === newValue);
                            setData({ 
                              ...data, 
                              sales_person: newValue || "",
                              sales_person_id: selectedMaster ? selectedMaster.id : null
                            });
                          }}
                          onInputChange={(event, newInputValue) => {
                            // 自由入力の場合はIDをnullに設定
                            const selectedMaster = salesPersonOptionsWithId.find(option => option.name === newInputValue);
                            setData({ 
                              ...data, 
                              sales_person: newInputValue || "",
                              sales_person_id: selectedMaster ? selectedMaster.id : null
                            });
                          }}
                          options={salesPersonOptions}
                          loading={salesPersonLoading}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {salesPersonLoading ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      ) : (
                        data.sales_person
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* 作業者情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">作業者情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  {data.workers!.map((worker, index) => (
                    <React.Fragment key={worker.id}>
                      <TableRow>
                        <StyledTableCell colSpan={2} className="section-header">
                          <SectionHeader index={index} type="worker" />
                        </StyledTableCell>
                      </TableRow>
                      {/* 作業者名と会社名は変更なし */}
                      {[
                        { label: "作業者名", key: "worker_name" },
                        { label: "会社名", key: "company_name" },
                      ].map((item) => (
                        <TableRow key={item.key}>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            {item.label}
                          </StyledTableCell>
                          <TableCell>
                            {renderEditableField(
                              worker[item.key as keyof typeof worker],
                              (value) => {
                                const newWorkers = [...data.workers!];
                                newWorkers[index] = { ...worker, [item.key]: value };
                                setData({ ...data, workers: newWorkers });
                              }
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              {renderSectionFooter(addWorker, "作業者を追加")}
            </AccordionDetails>
          </StyledAccordion>

          {/* その他作業情報 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">その他作業情報</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {data.other_works?.map((work, index) => (
                <StyledAccordion
                  key={work.id}
                  expanded={expandedUnits[`other-${work.id}`] ?? false}
                  onChange={(event: React.SyntheticEvent, isExpanded: boolean) => {
                    setExpandedUnits(prev => ({ ...prev, [`other-${work.id}`]: isExpanded }));
                  }}
                  sx={{ mb: index < data.other_works!.length - 1 ? 2 : 0 }}
                >
                  <StyledAccordionSummary expandIcon={<ExpandMore />}>
                    <SectionHeader index={index} type="other_work" />
                  </StyledAccordionSummary>
                  <AccordionDetails>
                    <Table>
                      <TableBody>
                        {/* 基本情報 */}
                        <TableRow>
                          <StyledTableCell colSpan={2} className="section-header">
                            基本情報
                          </StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            作業名称
                          </StyledTableCell>
                          <TableCell>
                            {isEditing ? (
                              <Autocomplete<string, false, false, true>
                                freeSolo
                                loading={workTypeLoading}
                                options={workTypeOptions}
                                value={String(work.work_type || '')}
                                inputValue={String(work.work_type || '')}
                                onChange={(_, newValue) => {
                                  const newWorks = [...data.other_works!];
                                  // 作業名称マスタから選択された場合はIDも設定
                                  const selectedMaster = workTypeOptionsWithId.find(option => option.name === newValue);
                                  newWorks[index] = { 
                                    ...work, 
                                    work_type: newValue || '',
                                    work_type_id: selectedMaster ? selectedMaster.id : null
                                  };
                                  setData({ ...data, other_works: newWorks });
                                }}
                                onInputChange={(_, newInputValue) => {
                                  const newWorks = [...data.other_works!];
                                  // 自由入力の場合はIDをnullに設定
                                  const selectedMaster = workTypeOptionsWithId.find(option => option.name === newInputValue);
                                  newWorks[index] = { 
                                    ...work, 
                                    work_type: newInputValue || '',
                                    work_type_id: selectedMaster ? selectedMaster.id : null
                                  };
                                  setData({ ...data, other_works: newWorks });
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    fullWidth
                                    size="small"
                                  />
                                )}
                              />
                            ) : (
                              <Typography>{String(work.work_type || '')}</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            作業内容
                          </StyledTableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={work.details || ''}
                                onChange={(e) => {
                                  const newWorks = [...data.other_works!];
                                  newWorks[index] = { ...work, details: e.target.value };
                                  setData({ ...data, other_works: newWorks });
                                }}
                                size="small"
                              />
                            ) : (
                              work.details || ''
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            数量
                          </StyledTableCell>
                          <TableCell>
                            {renderEditableField(
                              work.quantity,
                              (value) => {
                                if (data.other_works) {
                                  const newItems = [...data.other_works];
                                  newItems[index] = { ...work, quantity: Number(value) };
                                  setData({ ...data, other_works: newItems });
                                }
                              },
                              'number'
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell component="th" scope="row" className="item-label">
                            単位
                          </StyledTableCell>
                          <TableCell>
                            {renderEditableField(
                              work.unit,
                              (value) => {
                                if (data.other_works) {
                                  const newItems = [...data.other_works];
                                  newItems[index] = { ...work, unit: value };
                                  setData({ ...data, other_works: newItems });
                                }
                              }
                            )}
                          </TableCell>
                        </TableRow>

                        {/* 写真セクション */}
                        <TableRow>
                          <StyledTableCell colSpan={2} className="section-header">
                            写真
                          </StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2}>
                            {work.photo_sets?.map((photoSet, photoIndex) => (
                              <Box key={photoSet.id} mb={photoIndex < (work.photo_sets?.length || 0) - 1 ? 4 : 0}>
                                <Table>
                                  <TableBody>
                                    {/* 写真セットのヘッダー行に削除ボタンを追加 */}
                                    <TableRow>
                                      <StyledTableCell colSpan={2} className="section-header">
                                        <Box
                                          display="flex"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          width="100%"
                                        >
                                          <Typography variant="subtitle1">
                                            写真セット {photoIndex + 1}
                                          </Typography>
                                          {isEditing && (
                                            <Button
                                              startIcon={<Delete />}
                                              color="error"
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick('other_photo_set', photoIndex, index);
                                              }}
                                              sx={{ ml: 2 }}
                                            >
                                              削除
                                            </Button>
                                          )}
                                        </Box>
                                      </StyledTableCell>
                                    </TableRow>
                                    <TableRow>
                                      <StyledTableCell component="th" scope="row" className="item-label">
                                        タイトル
                                      </StyledTableCell>
                                      <TableCell>
                                        {isEditing ? (
                                          <TextField
                                            fullWidth
                                            value={photoSet.title || ''}
                                            onChange={(e) => {
                                              const newPhotoSets = [...(work.photo_sets || [])];
                                              newPhotoSets[photoIndex] = {
                                                ...photoSet,
                                                title: e.target.value,
                                              };
                                              const newWorks = [...data.other_works!];
                                              newWorks[index] = { ...work, photo_sets: newPhotoSets };
                                              setData({ ...data, other_works: newWorks });
                                            }}
                                            size="small"
                                          />
                                        ) : (
                                          <Typography>{photoSet.title || ''}</Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell colSpan={2}>
                                        {renderPhotoSetContent(photoSet, (updatedPhotoSet) => {
                                          const newPhotoSets = [...(work.photo_sets || [])];
                                          newPhotoSets[photoIndex] = updatedPhotoSet;
                                          const newWorks = [...data.other_works!];
                                          newWorks[index] = { ...work, photo_sets: newPhotoSets };
                                          setData({ ...data, other_works: newWorks });
                                        })}
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </Box>
                            ))}
                            {isEditing && (
                              <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => {
                                    const newPhotoSet = {
                                      id: Date.now(),
                                      title: "",
                                      before_image_path: null,
                                      before_comment: "",
                                      middle_image_path: null,
                                      middle_comment: "",
                                      after_image_path: null,
                                      after_comment: "",
                                    };
                                    const newPhotoSets = [...(work.photo_sets || []), newPhotoSet];
                                    const newWorks = [...data.other_works!];
                                    newWorks[index] = { ...work, photo_sets: newPhotoSets };
                                    setData({ ...data, other_works: newWorks });
                                  }}
                                  startIcon={<Add />}
                                  size="small"
                                >
                                  写真を追加
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </StyledAccordion>
              ))}
              {renderSectionFooter(addOtherWork, "その他作業を追加")}
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">報告事項</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      報告事項
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={data.points_to_note || ''}
                          onChange={(e) => setData({ ...data, points_to_note: e.target.value })}
                          size="small"
                        />
                      ) : (
                        data.points_to_note || ''
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* フード情報 */}
          {data.food_units && (
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">フード情報</Typography>
              </StyledAccordionSummary>
              <AccordionDetails>
                {data.food_units.map((unit, index) => (
                  <StyledAccordion
                    key={unit.id}
                    expanded={expandedUnits[`food-${unit.id}`] ?? false}
                    onChange={(event: React.SyntheticEvent, isExpanded: boolean) => {
                      setExpandedUnits(prev => ({ ...prev, [`food-${unit.id}`]: isExpanded }));
                    }}
                    sx={{ mb: index < data.food_units!.length - 1 ? 2 : 0 }}
                  >
                    <StyledAccordionSummary expandIcon={<ExpandMore />}>
                      <SectionHeader
                        index={index}
                        type="food_unit"
                      />
                    </StyledAccordionSummary>
                    <AccordionDetails>
                      <Table>
                        <TableBody>
                          {/* 1. 基本情報セクション（最初に表示） */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              基本情報
                            </StyledTableCell>
                          </TableRow>
                          <TableRow>
                            <StyledTableCell component="th" scope="row" className="item-label">
                              管理No
                            </StyledTableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  value={unit.management_no || ''}
                                  onChange={(e) => {
                                    const newUnits = [...data.food_units!];
                                    newUnits[index] = { ...unit, management_no: e.target.value };
                                    setData({ ...data, food_units: newUnits });
                                  }}
                                  size="small"
                                />
                              ) : (
                                <Typography>{unit.management_no || ''}</Typography>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* 2. 写真セクション（基本情報の後に表示） */}
                          <TableRow>
                            <StyledTableCell colSpan={2} className="section-header">
                              写真
                            </StyledTableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2}>
                              {unit.photo_sets?.map((photoSet, photoIndex) => (
                                <Box key={photoSet.id} mb={photoIndex < (unit.photo_sets?.length || 0) - 1 ? 4 : 0}>
                                  <Table>
                                    <TableBody>
                                      {/* 写真セットのヘッダー行に削除ボタンを追加 */}
                                      <TableRow>
                                        <StyledTableCell colSpan={2} className="section-header">
                                          <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            width="100%"
                                          >
                                            <Typography variant="subtitle1">
                                              写真セット {photoIndex + 1}
                                            </Typography>
                                            {isEditing && (
                                              <Button
                                                startIcon={<Delete />}
                                                color="error"
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteClick('food_photo_set', photoIndex, index);
                                                }}
                                                sx={{ ml: 2 }}
                                              >
                                                削除
                                              </Button>
                                            )}
                                          </Box>
                                        </StyledTableCell>
                                      </TableRow>
                                      <TableRow>
                                        <StyledTableCell component="th" scope="row" className="item-label">
                                          タイトル
                                        </StyledTableCell>
                                        <TableCell>
                                          {isEditing ? (
                                            <TextField
                                              fullWidth
                                              value={photoSet.title || ''}
                                              onChange={(e) => {
                                                const newPhotoSets = [...(unit.photo_sets || [])];
                                                newPhotoSets[photoIndex] = {
                                                  ...photoSet,
                                                  title: e.target.value,
                                                };
                                                const newUnits = [...data.food_units!];
                                                newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                                setData({ ...data, food_units: newUnits });
                                              }}
                                              size="small"
                                            />
                                          ) : (
                                            <Typography>{photoSet.title || ''}</Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={2}>
                                          {renderPhotoSetContent(photoSet, (updatedPhotoSet) => {
                                            const newPhotoSets = [...(unit.photo_sets || [])];
                                            newPhotoSets[photoIndex] = updatedPhotoSet;
                                            const newUnits = [...data.food_units!];
                                            newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                            setData({ ...data, food_units: newUnits });
                                          })}
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </Box>
                              ))}
                              {isEditing && (
                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                      const newPhotoSet = {
                                        id: Date.now(),
                                        title: "",
                                        before_image_path: null,
                                        before_comment: "",
                                        middle_image_path: null,
                                        middle_comment: "",
                                        after_image_path: null,
                                        after_comment: "",
                                        food_unit_id: unit.id
                                      };
                                      const newPhotoSets = [...(unit.photo_sets || []), newPhotoSet];
                                      const newUnits = [...data.food_units!];
                                      newUnits[index] = { ...unit, photo_sets: newPhotoSets };
                                      setData({ ...data, food_units: newUnits });
                                    }}
                                    startIcon={<Add />}
                                    size="small"
                                  >
                                    写真を追加
                                  </Button>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </StyledAccordion>
                ))}
                {renderSectionFooter(addFoodUnit, "フードを追加")}
              </AccordionDetails>
            </StyledAccordion>
          )}


          {/* ガラス・その他の写真 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">ガラス・その他の写真</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              {data.photo_sets?.map((photo, index) => (
                <Box key={photo.id} mb={index < data.photo_sets!.length - 1 ? 4 : 0}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <StyledTableCell colSpan={2} className="section-header">
                          <SectionHeader index={index} type="photo" />
                        </StyledTableCell>
                      </TableRow>
                      <TableRow>
                        <StyledTableCell component="th" scope="row" className="item-label">
                          タイトル
                        </StyledTableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              value={photo.title || ''}
                              onChange={(e) => {
                                const newPhotos = [...data.photo_sets!];
                                newPhotos[index] = { ...photo, title: e.target.value };
                                setData({ ...data, photo_sets: newPhotos });
                              }}
                              size="small"
                            />
                          ) : (
                            <Typography>{photo.title || ''}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}>
                          {renderPhotoSetContent(photo, (updatedPhotoSet) => {
                            const newPhotos = [...data.photo_sets!];
                            newPhotos[index] = updatedPhotoSet;
                            setData({ ...data, photo_sets: newPhotos });
                          })}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              ))}
              {renderSectionFooter(addPhotoSet, "ガラス・その他の写真を追加")}
            </AccordionDetails>
          </StyledAccordion>


          {/* 作業者メモ欄 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">作業者メモ</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      作業者メモ
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={data.worker_memo || ''}
                          onChange={(e) => setData({ ...data, worker_memo: e.target.value })}
                          size="small"
                        />
                      ) : (
                        <Typography
                          component="div"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {data.worker_memo || ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  ※報告書には印字されません
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion>

          {/* サイン画像 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">サイン画像</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      サイン画像
                    </StyledTableCell>
                    <TableCell>
                      {data.signature && (
                        <img
                          src={`data:image/jpeg;base64,${data.signature}`}
                          alt="サイン"
                          style={{ maxWidth: '100%', maxHeight: '200px' }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>

          {/* メモ欄 */}
          <StyledAccordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">管理者メモ</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Table>
                <TableBody>
                  <TableRow>
                    <StyledTableCell component="th" scope="row" className="item-label">
                      管理者メモ
                    </StyledTableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={data.memo || ''}
                          onChange={(e) => setData({ ...data, memo: e.target.value })}
                          size="small"
                        />
                      ) : (
                        <Typography
                          component="div"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {data.memo || ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  ※報告書には印字されません
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        </>
      )}
    </PageContainer>
  );
};

export default ReportFreeDetail;